import axios from "axios";
import { CLEAR_OFFICER_PROFILE, GET_OFFICER_PROFILE, LOGIN_FAILURE, LOGIN_SUCCESS, LOGOUT } from "../actionType";
import { toast } from "react-toastify";

export const loginSuccess = (token, user) => ({
    type: LOGIN_SUCCESS,
    payload: { token, user },
});

export const loginFailure = () => ({
    type: LOGIN_FAILURE,
});

// A short per-browser suffix, just to tell apart two devices that would otherwise render an
// identical label (e.g. two Chrome-on-macOS machines). Not an auth key — safe to keep in
// localStorage unconditionally (does not affect the flag-off rollback story).
const getOrCreateDeviceSuffix = () => {
    try {
        let id = localStorage.getItem('seeds_device_id');
        if (!id) {
            id = Math.random().toString(16).slice(2, 8);
            localStorage.setItem('seeds_device_id', id);
        }
        return id;
    } catch {
        return Math.random().toString(16).slice(2, 8);
    }
};

// Builds the human-readable label stored as Sessions.device_fingerprint and shown as-is in the
// Active Sessions list (ActiveSessionsModal.jsx) — no separate UA-parsing needed on the display
// side. Kept here (not just at display time) because it's derived at login, when navigator.userAgent
// is actually available to us; the backend only stores whatever string it's given.
const describeThisDevice = () => {
    const ua = (navigator.userAgent || '').toLowerCase();
    const os = ua.includes('windows') ? 'Windows'
        : ua.includes('mac os') || ua.includes('macintosh') ? 'macOS'
        : ua.includes('android') ? 'Android'
        : ua.includes('iphone') || ua.includes('ipad') ? 'iOS'
        : ua.includes('linux') ? 'Linux'
        : null;
    const browser = ua.includes('edg') ? 'Edge'
        : ua.includes('chrome') ? 'Chrome'
        : ua.includes('firefox') ? 'Firefox'
        : ua.includes('safari') ? 'Safari'
        : null;
    const label = os && browser ? `${browser} on ${os}` : (browser || os || 'Browser');
    return `${label} · ${getOrCreateDeviceSuffix()}`;
};

// Replicates the sessionStorage-always / localStorage-if-remembered write pattern from
// login() below, so login() and the silent-refresh interceptor don't duplicate it.
export const persistTokens = (tokens) => {
    sessionStorage.setItem('tokens', JSON.stringify(tokens));
    const remembered = sessionStorage.getItem('remember_me') === 'true' || localStorage.getItem('remember_me') === 'true';
    if (remembered) {
        localStorage.setItem('tokens', JSON.stringify(tokens));
    }
};

export const logout = async (dispatch) => {
    try{
        dispatch({ type: LOGOUT });

        // Feature-detect Keycloak session mode from the stored token shape (see login() below) —
        // when the backend flag is off, tokens never carry session_id, so this stays a no-op and
        // logout behaves exactly as it did before session management existed.
        try {
            const stored = JSON.parse(sessionStorage.getItem('tokens') || localStorage.getItem('tokens') || 'null');
            if (stored?.session_id) {
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/officer/logout`, {}, {
                    headers: { Authorization: `Bearer ${stored.token}` },
                });
            }
        } catch (e) {
            // Best-effort — local logout must still succeed even if the API call fails.
            console.error('Logout API call failed (continuing local cleanup):', e);
        }

        toast.success("User logged out");

        sessionStorage.removeItem('tokens');
        sessionStorage.removeItem('isAuthenticated');
        sessionStorage.removeItem('officer');
        sessionStorage.removeItem('remember_me');

        localStorage.removeItem('tokens');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('officer');
        localStorage.removeItem('remember_me');

        sessionStorage.removeItem('applicationFilters');
        sessionStorage.removeItem('applicationStageFilters');
        sessionStorage.removeItem('applicationTrancheLimit');
        sessionStorage.removeItem('branchApplicationFilters');
        sessionStorage.removeItem('leadFilters');
        sessionStorage.removeItem('currentPage');
        sessionStorage.removeItem('leadsCurrentPage');
        sessionStorage.removeItem('leadLimit');
        sessionStorage.removeItem('leadsTotalCount');
        sessionStorage.removeItem('leadsTotalPages');
        sessionStorage.removeItem('limit');
        sessionStorage.removeItem('totalCount');
        sessionStorage.removeItem('totalPages');
        sessionStorage.removeItem('rejectedApplicationFilters');

        sessionStorage.removeItem("taskDashboardState");
        sessionStorage.removeItem("deletedApplicationFilters");

        sessionStorage.removeItem("allRolesLimit");
        sessionStorage.removeItem("officerFilters");
        sessionStorage.removeItem("roleFilters");
        sessionStorage.removeItem("fromRoute");

        // Reset sidebar state to collapsed on logout
        localStorage.setItem('sidebar_expanded', 'false');

        // Clear officer profile so previous officer's permission set 
        // doesn't pick up stale data into the next login
        dispatch({ type: CLEAR_OFFICER_PROFILE });

        return true;
    }
    catch(err){
        toast.error("Logout failed");
        return false;
    }
};

// Thunk for async login with 'remember' parameter for persistent session
export const login = (email, password, remember = false) => async (dispatch) => {
    try {
        const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/officer/login/password`, {
            email,
            password,
            device_info: { fingerprint: describeThisDevice() },
            app_info: { platform: 'web', user_agent: navigator.userAgent },
        });

        if(res.data?.data){
            const { tokens, officer } = res.data.data;

            // Access denied if officer has no permissions at all
            if (!officer?.permissions?.length) {
                toast.error("Access Denied");
                return null;
            }

            dispatch(loginSuccess(tokens.token, officer));

            // Immediately hydrate the profile + officer permission set in Redux
            dispatch({ type: GET_OFFICER_PROFILE, payload: officer });

            toast.success("Login successful");
    
            localStorage.setItem('sidebar_expanded', 'false');

            // Always populate sessionStorage for active tab API calls
            sessionStorage.setItem('tokens', JSON.stringify(tokens));
            sessionStorage.setItem('officer', JSON.stringify(officer));
            sessionStorage.setItem('isAuthenticated', JSON.stringify(true));
            sessionStorage.setItem('remember_me', JSON.stringify(remember));

            if (remember) {
                // Save to localStorage so session persists across tab/browser restarts
                localStorage.setItem('tokens', JSON.stringify(tokens));
                localStorage.setItem('officer', JSON.stringify(officer));
                localStorage.setItem('isAuthenticated', JSON.stringify(true));
                localStorage.setItem('remember_me', JSON.stringify(true));
            } else {
                localStorage.removeItem('tokens');
                localStorage.removeItem('officer');
                localStorage.removeItem('isAuthenticated');
                localStorage.removeItem('remember_me');
            }

            return officer;
        }
    }
    catch (error) {
        console.error('Login failed:', error);

        let errorMsg = error?.response?.data?.message;

        dispatch(loginFailure());
        toast.error(`${errorMsg}`);

        return null;
    }
};
